import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { BrandButton } from "../components/ui/BrandButton";
import { FormCard } from "../components/form/FormCard";
import { brandInputClass } from "../components/form/fieldStyles";
import { useAppLoading } from "../context/AppLoadingContext";

export function PhoneRepairPage() {
  const navigate = useNavigate();
  const { trackAsync } = useAppLoading();
  const { phoneRepair, completePhoneRepair } = useAuth();
  const [lastDigit, setLastDigit] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!phoneRepair?.shouldShow) {
      navigate("/home", { replace: true });
    }
  }, [navigate, phoneRepair?.shouldShow]);

  useEffect(() => {
    setLastDigit("");
    setFieldError("");
    setSubmitError("");
  }, [phoneRepair?.displayValue, phoneRepair?.field]);

  const previewValue = useMemo(
    () => String(phoneRepair?.displayValue ?? "").trim(),
    [phoneRepair?.displayValue],
  );

  const handleChange = (event) => {
    const value = event.target.value.replace(/\D/g, "").slice(0, 1);
    setLastDigit(value);
    setFieldError("");
    setSubmitError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!previewValue) {
      setSubmitError("No pudimos leer el número pendiente.");
      return;
    }

    if (!lastDigit) {
      setFieldError("Ingresá el último dígito.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const response = await trackAsync(() => completePhoneRepair({ lastDigit }));

      if (response?.phoneRepair?.shouldShow) {
        setLastDigit("");
        return;
      }

      navigate("/home", { replace: true });
    } catch (error) {
      if (error.details && typeof error.details === "object" && !Array.isArray(error.details)) {
        if (error.details.field === "phoneNumber") {
          setFieldError(error.message);
          return;
        }
      }

      setSubmitError(error.message || "No pudimos completar la reparación.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!phoneRepair?.shouldShow) {
    return <Navigate to="/home" replace />;
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(123,191,252,0.18),_transparent_34%),linear-gradient(180deg,_#f7fbff_0%,_#eef6ff_100%)] px-4 py-6 text-brand-primary sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-3xl items-center justify-center">
        <FormCard className="relative w-full overflow-hidden rounded-[2rem] border border-brand-secondary/15 bg-white/92 px-6 py-7 shadow-[0_24px_80px_rgba(20,50,135,0.18)] backdrop-blur-sm sm:px-8 sm:py-8">
          <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-brand-gold via-brand-accent-light to-brand-secondary" />

          <div className="grid gap-6">
            <div className="grid gap-3">
              <h1 className="font-display text-4xl leading-[0.92] sm:text-5xl">
                Verificá tu celular
              </h1>
              <p className="max-w-2xl text-base leading-7 text-slate-600">
                Para continuar necesitamos que verifiques el último dígito de tu número de
                celular.
              </p>
            </div>

            {submitError ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {submitError}
              </div>
            ) : null}

            <form className="grid gap-5" onSubmit={handleSubmit}>
              <div className="rounded-[1.75rem] border border-brand-secondary/15 bg-brand-surface px-5 py-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5">
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 text-base leading-7 text-slate-600 sm:text-lg">
                    <span className="font-medium text-brand-primary">
                      {phoneRepair?.label || "Celular"}:
                    </span>
                    <span className="font-mono font-semibold tracking-[0.08em] text-brand-primary">
                      {previewValue}
                    </span>
                  </div>

                  <div className="w-full sm:w-[150px] sm:shrink-0">
                    <label className="sr-only" htmlFor="lastDigit">
                      Último dígito
                    </label>
                    <input
                      id="lastDigit"
                      name="lastDigit"
                      value={lastDigit}
                      onChange={handleChange}
                      type="tel"
                      inputMode="numeric"
                      autoComplete="off"
                      required
                      placeholder="0"
                      aria-invalid={Boolean(fieldError)}
                      className={brandInputClass}
                    />
                  </div>
                </div>

                {fieldError ? (
                  <p className="mt-2 text-xs font-medium text-rose-600">{fieldError}</p>
                ) : null}
              </div>

              <div className="flex justify-end">
                <BrandButton
                  type="submit"
                  disabled={isSubmitting}
                  variant="primary"
                  radius="xl"
                  size="xl"
                  className="min-w-[220px] uppercase tracking-[0.12em]"
                >
                  {isSubmitting ? "Confirmando..." : "Confirmar"}
                </BrandButton>
              </div>
            </form>
          </div>
        </FormCard>
      </div>
    </div>
  );
}
